/**
 * TrivenyWorkspaceAdmin — one surface for the operator's people + orgs.
 *
 * Combines the sign-up directory (Organizations) and workspace members/invites
 * (Team) behind a single tabbed page, since they belong together. Routed from
 * both the Org page (org-settings) and Team (platform-team); each just picks the
 * initial tab. Organizations is operator-only (the inner component 403s
 * gracefully for non-operators).
 */

import { useState } from 'react';
import { Building2, Users } from 'lucide-react';
import { tvTokens } from '@/src/platform/shared/components/triveny';
import TrivenyOrganizations from './TrivenyOrganizations';
import TrivenyTeamPanel from './TrivenyTeamPanel';

type Tab = 'organizations' | 'team';

export default function TrivenyWorkspaceAdmin({ initialTab = 'organizations' }: { initialTab?: Tab } = {}) {
  const [tab, setTab] = useState<Tab>(initialTab);
  const TABS: { id: Tab; label: string; icon: typeof Users }[] = [
    { id: 'organizations', label: 'Organizations', icon: Building2 },
    { id: 'team', label: 'Team', icon: Users },
  ];

  return (
    <div className="triveny-platform" style={{ minHeight: '100vh', background: tvTokens.paper, fontFamily: tvTokens.fontSans, color: tvTokens.ink }}>
      <div
        role="tablist"
        aria-label="Workspace admin"
        style={{ display: 'flex', gap: 4, padding: '14px 22px 0', borderBottom: `1px solid ${tvTokens.borderMuted}`, background: tvTokens.surface }}
      >
        {TABS.map((t) => {
          const active = tab === t.id;
          const Icon = t.icon;
          return (
            <button
              key={t.id}
              type="button"
              role="tab"
              className="tv-underline-tab"
              aria-selected={active}
              onClick={() => setTab(t.id)}
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 8,
                padding: '10px 16px 12px',
                background: 'transparent',
                border: 0,
                borderBottom: `2px solid ${active ? tvTokens.accent : 'transparent'}`,
                cursor: 'pointer',
                fontFamily: tvTokens.fontDisplay,
                fontSize: 13.5,
                fontWeight: 600,
                color: active ? tvTokens.ink : tvTokens.ink500,
              }}
            >
              <Icon size={15} /> {t.label}
            </button>
          );
        })}
      </div>
      {tab === 'organizations' ? <TrivenyOrganizations /> : <TrivenyTeamPanel />}
    </div>
  );
}
